// The index card (IndexCard on the Mac): the title (with its alternates, a snowflake when cold, a folder mark), the
// synopsis in serif, a hairline against its word target (verdigris once met), "POV · status" and the words (a
// folder's whole subtree); a 3 pt top edge in the point of view's colour (else the label's), and the heatmap's
// terracotta wash behind the text. Colours are set through the CSSOM (the CSP allows no style attributes).
//
// A character's study page is a cast card instead (CastCard on the Mac): the character's colour along the top, their
// portrait (the page's first photo on the Mac and iPad; the web has no photos, so a placeholder in their colour), their
// name and role, and how many of the manuscript's scenes they're in — not the page's boilerplate synopsis.
import { entityCSSColor, fragmentTags, type Entity, type Field, type OpenedProject, type Project, type TreeNode } from '@writing-suite/core';
import { WordTarget } from '@writing-suite/core/plan';
import { castCardLine, shortName, studiedCharacter } from '@writing-suite/core/story';
import { storyModel } from '../../store/story.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { navigate } from '../../shell/route.ts';
import { TagChip } from '../FieldEditor.tsx';
import type { MenuEntry } from '../Menu.tsx';
import { cssColor, formatNumber, hasChildren } from '../helpers.ts';

type StoryEntity = OpenedProject['record']['entities'][number];

/** `studiedCharacter(of:)`: the character whose study page the card's document is, if it's one. */
export function cardCharacter(project: Project, node: TreeNode): StoryEntity | null {
  if (!node.fragmentId) return null;
  const entities = (project as Partial<OpenedProject>).record?.entities ?? [];
  return studiedCharacter(project.fragment(node.fragmentId)?.metadata, entities);
}

/** A cast card's figures: the manuscript's scenes the character is in, and those told from their point of view. */
export const castAppearances = (project: Project, character: StoryEntity) => storyModel(project).appearances.get(character.id) ?? { scenes: 0, pov: 0 };

/** A cast card's menu (the Mac's PlanCard): the character's study, or the character in Cast & Places; null for any other
 *  card. */
export function castCardMenu(props: Pick<PageProps, 'project' | 'hrefFor'>, node: TreeNode): MenuEntry[] | null {
  const character = cardCharacter(props.project, node);
  if (!character) return null;
  return [
    { label: 'Open Character Study', onSelect: () => navigate(props.hrefFor('plan/study', null, { who: character.id })) },
    { label: 'Show in Cast & Places', onSelect: () => navigate(props.hrefFor('plan/cast', null, { who: character.id })) },
  ];
}

/** What's on a cast card. */
export function CastCardFace({ project, character }: { project: Project; character: StoryEntity }) {
  const seen = castAppearances(project, character);
  const colour = entityCSSColor(character.color);
  const role = character.role?.trim();
  return (
    <>
      <span className="card-edge" aria-hidden="true" ref={(el) => el?.style.setProperty('--edge', colour)} />
      <span className="cast-card" data-cast-card={character.id}>
        <span className="cast-portrait" aria-hidden="true" data-portrait="placeholder" ref={(el) => el?.style.setProperty('--dot', colour)}>
          {character.name.trim()[0]?.toUpperCase() ?? ''}
        </span>
        <span className="cast-card-text">
          <span className="card-title">
            <span className="card-title-text">{character.name || 'Unnamed character'}</span>
          </span>
          {role ? <span className="card-synopsis">{role}</span> : <span className="card-synopsis none">No role yet</span>}
          <span className="card-foot">
            <span data-scenes={seen.scenes} data-pov={seen.pov}>{castCardLine(seen)}</span>
          </span>
        </span>
      </span>
    </>
  );
}

/** The card's edge colour, its "POV · status" line and its words. */
export function cardFacts(project: Project, node: TreeNode, fields: Field[], entities: Entity[]) {
  const fragment = node.fragmentId ? project.fragment(node.fragmentId) : null;
  const meta = fragment?.metadata ?? {};
  const povValue = meta.pov?.type === 'text' ? meta.pov.value : null;
  const pov = povValue ? entities.find((e) => e.id === povValue) : undefined;
  const labelField = fields.find((f) => f.id === 'label');
  const labelValue = meta.label?.type === 'text' ? meta.label.value : null;
  const label = labelValue ? labelField?.options.find((o) => o.id === labelValue || o.name === labelValue) : undefined;
  const edge = cssColor(pov?.color) ?? cssColor(label?.color);
  const status = meta.status ? project.displayValue('status', meta.status) : '';
  const povName = pov ? shortName(pov) : '';
  const line = [povName, status].filter(Boolean).join(' · ');
  const folder = hasChildren(node);
  const words = folder ? project.subtreeWords(node) : node.fragmentId ? project.words(node.fragmentId) : 0;
  const target = WordTarget.value(meta);
  // The document's tags (choosing one shows everything tagged with it), read from the record so a tag another
  // device added before syncing shows too.
  const record = node.fragmentId ? (project as Partial<OpenedProject>).fragmentRecord?.(node.fragmentId) : null;
  const tags = record ? fragmentTags(record) : [];
  return { fragment, edge, line, words, folder, target, povName, status, tags };
}

/** A progress hairline: words against a target (verdigris once met). */
export function TargetBar({ words, target, className = 'target-bar' }: { words: number; target: number; className?: string }) {
  const met = words >= target;
  const fraction = Math.max(0, Math.min(1, words / target));
  return (
    <span
      className={`${className}${met ? ' met' : ''}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={target}
      aria-valuenow={Math.min(words, target)}
      aria-label={`${formatNumber(words)} of ${formatNumber(target)} words`}
      title={`${formatNumber(words)} of ${formatNumber(target)} words`}
      data-met={met || undefined}
    >
      <span className="target-fill" ref={(el) => el?.style.setProperty('--fill', `${(fraction * 100).toFixed(1)}%`)} />
    </span>
  );
}

export interface CardLook {
  heat?: number;
  cold?: boolean;
  coldDays?: number;
  /** Members in the node's alternate set (shown when there's more than one). */
  alternates?: number;
}

/** What's on a card: its spans, to put inside a link or a draggable box. */
export function CardFace({ project, node, fields, entities, look = {} }: { project: Project; node: TreeNode; fields: Field[]; entities: Entity[]; look?: CardLook }) {
  const character = cardCharacter(project, node);
  if (character) return <CastCardFace project={project} character={character} />;
  const f = cardFacts(project, node, fields, entities);
  return (
    <>
      {f.edge ? <span className="card-edge" aria-hidden="true" ref={(el) => el?.style.setProperty('--edge', f.edge)} /> : null}
      <span className="card-title">
        <span className="card-title-text">{node.title || 'Untitled'}</span>
        {f.folder ? <span className="card-folder"> · {node.children.length === 1 ? '1 item' : `${node.children.length} items`}</span> : null}
        {look.alternates && look.alternates > 1 ? (
          <span className="card-badge alternates" title={`${look.alternates} alternates`} aria-label={`${look.alternates} alternates`}>
            ⑂ {look.alternates}
          </span>
        ) : null}
        {look.cold ? (
          <span className="card-badge cold" data-cold title={`Not opened or changed in ${look.coldDays ?? 90} days`} aria-label={`Cold: not opened or changed in ${look.coldDays ?? 90} days`}>
            {'\u2744\uFE0E'}
          </span>
        ) : null}
      </span>
      {f.fragment?.synopsis.trim() ? <span className="card-synopsis">{f.fragment.synopsis}</span> : <span className="card-synopsis none">No synopsis yet</span>}
      {f.tags.length ? (
        <span className="card-tags tag-chips">
          {f.tags.map((t) => (
            <TagChip key={t} tag={t} />
          ))}
        </span>
      ) : null}
      {f.target !== null ? <TargetBar words={f.words} target={f.target} /> : null}
      <span className="card-foot">
        <span>{f.line}</span>
        <span className="card-words">{f.words > 0 ? formatNumber(f.words) : '—'}</span>
      </span>
    </>
  );
}

/** The card's heat as a class (`heat-1`…`heat-4`), for the wash. */
export const heatClass = (level: number | undefined) => (level && level > 0 ? ` heat heat-${Math.min(4, level)}` : '');
